// A place's asset board (CR_Manager_vs_User_role §5.2 Assets tab; was the site board): the Receive dialog, the zone
// bulk bar and the list of everything that belongs here or is here now.
import { ASSET_DISPLAY_STATES, type AssetDisplayState } from '@fleetspoke/ui/tokens';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../../auth/session';
import { AssetThumb } from '../asset/AssetThumb';
import { ZoneSignsDialog } from '../LabelsDialog';
import { Empty, Loading, LoadError } from '../Page';
import { OpenTransferChip, StateChip } from '../StateChip';
import { ZoneText } from '../ZoneText';
import { useFirstPhotoUrls } from '../../data/photos';
import { type AssetOverview, keys, type SiteOverview, useSiteBoard } from '../../data/queries';
import {
  activeZones,
  type SetZoneResult,
  type SiteZone,
  useSetAssetZone,
  useZones,
} from '../../data/zones';
import { withParam } from '../../lib/assetFilters';
import { supabase } from '../../lib/supabase';
import { dateTime, relativeTime } from '../../lib/time';
import { canActAt, inZone, NO_ZONE, zoneCounts } from '../../lib/zones';

/**
 * The State column: a QR-only asset reads where it was last seen — a scan, or a stock count that confirmed it (so a
 * counted asset is "on site", not "QR only"); what it carries is the Label column. Tagged assets: display_state.
 */
function placeState(a: AssetOverview): AssetDisplayState {
  return a.display_state === 'untagged' ? (a.seen_state ?? 'unknown') : a.display_state;
}
const PLACE_STATES = ASSET_DISPLAY_STATES.filter((s) => s !== 'untagged');

interface OpenTransfer {
  id: string;
  ref: string;
  status: string;
  from_site_id: string | null;
  created_by_system: boolean;
  transfer_lines: { asset_id: string; status: string; assets: { asset_code: string } | null }[];
}

function useIncomingTransfers(siteId: string) {
  return useQuery({
    queryKey: ['incoming', siteId],
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('transfers')
        .select(
          'id, ref, status, from_site_id, created_by_system, transfer_lines(asset_id, status, assets(asset_code))',
        )
        .eq('to_site_id', siteId)
        .in('status', ['dispatched', 'awaiting_receipt'])
        .order('created_at');
      if (error) throw new Error(error.message);
      return data as unknown as OpenTransfer[];
    },
  });
}

/**
 * Receive dialog: pre-ticked from presence evidence; unticked lines become "not received" (transfer disputed).
 * Optionally puts the received assets into one zone (CR_20260914); a zone never costs the receipt.
 */
export function ReceiveDialog({
  siteId,
  board,
  zones,
  open,
  onClose,
}: {
  siteId: string;
  board: AssetOverview[];
  zones: SiteZone[];
  open: boolean;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const incoming = useIncomingTransfers(siteId);
  const here = useMemo(
    () =>
      new Set(
        board
          .filter((a) => a.presence_site_id === siteId && a.presence_state === 'on_site')
          .map((a) => a.asset_id),
      ),
    [board, siteId],
  );
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [zoneId, setZoneId] = useState('');

  const receive = useMutation({
    mutationFn: async (transfer: OpenTransfer) => {
      const pending = transfer.transfer_lines.filter((l) => l.status === 'pending');
      const ticked = pending
        .filter((l) => here.has(l.asset_id) && !unticked.has(l.asset_id))
        .map((l) => l.asset_id);
      const { data, error } = await supabase().rpc('receive_transfer', {
        p_idempotency_key: crypto.randomUUID(),
        p_transfer_id: transfer.id,
        p_asset_ids: ticked.length === pending.length ? null : ticked,
        p_method: 'tap',
        ...(zoneId ? { p_zone_id: zoneId } : {}),
      });
      if (error) throw new Error(error.message);
      return data as { received: number; zone_applied?: boolean };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incoming', siteId] });
      void queryClient.invalidateQueries({ queryKey: keys.board(siteId) });
      void queryClient.invalidateQueries({ queryKey: keys.sites });
      void queryClient.invalidateQueries({ queryKey: ['zone-moves'] });
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('board.receive_title')}</DialogTitle>
      <DialogContent dividers>
        {zones.length ? (
          <TextField
            select
            size="small"
            label={t('board.receive_into_zone')}
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value)}
            sx={{ minWidth: 240, mb: 2 }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            data-testid="receive-zone"
          >
            <MenuItem value="">{t('assets.zone_none')}</MenuItem>
            {zones.map((z) => (
              <MenuItem key={z.id} value={z.id}>
                {z.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {incoming.isLoading ? (
          <Loading />
        ) : incoming.error ? (
          <LoadError error={incoming.error} />
        ) : (
          (incoming.data ?? []).map((tr) => {
            const pending = tr.transfer_lines.filter((l) => l.status === 'pending');
            return (
              <Box key={tr.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">{tr.ref}</Typography>
                {pending.map((line) => {
                  const arrived = here.has(line.asset_id);
                  return (
                    <FormControlLabel
                      key={line.asset_id}
                      sx={{ display: 'flex' }}
                      control={
                        <Checkbox
                          checked={arrived && !unticked.has(line.asset_id)}
                          disabled={!arrived}
                          onChange={(e) =>
                            setUnticked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.delete(line.asset_id);
                              else next.add(line.asset_id);
                              return next;
                            })
                          }
                        />
                      }
                      label={`${line.assets?.asset_code ?? line.asset_id}${arrived ? '' : ` — ${t('state.in_transit').toLowerCase()}`}`}
                    />
                  );
                })}
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 1 }}
                  disabled={receive.isPending || !pending.some((l) => here.has(l.asset_id))}
                  onClick={() => receive.mutate(tr)}
                >
                  {t('board.receive_confirm')}
                </Button>
              </Box>
            );
          })
        )}
        {receive.error && (
          <Alert severity="error">
            {t('common.error_generic')} {(receive.error as Error).message}
          </Alert>
        )}
        {receive.data && (
          <Alert severity="success">{t('board.received', { count: receive.data.received })}</Alert>
        )}
        {receive.data?.zone_applied === false && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('board.zone_not_applied')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Bulk bar: move the ticked rows into a zone of this site, or out of their zone. */
function ZoneBulkBar({
  siteId,
  zones,
  selected,
  onDone,
}: {
  siteId: string;
  zones: SiteZone[];
  selected: string[];
  onDone(result: SetZoneResult): void;
}) {
  const { t } = useTranslation();
  const setZone = useSetAssetZone();
  const move = (zoneId: string | null) =>
    setZone.mutate({ siteId, assetIds: selected, zoneId }, { onSuccess: onDone });
  return (
    <Paper
      variant="outlined"
      sx={{ px: 1.5, py: 1, mb: 1 }}
      data-testid="zone-bulk-bar"
      role="region"
      aria-label={t('board.move_to_zone')}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('board.selected', { count: selected.length })}
        </Typography>
        <TextField
          select
          size="small"
          label={t('board.move_to_zone')}
          value=""
          disabled={setZone.isPending}
          onChange={(e) => move(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {zones.map((z) => (
            <MenuItem key={z.id} value={z.id}>
              {z.name}
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" disabled={setZone.isPending} onClick={() => move(null)}>
          {t('board.remove_from_zone')}
        </Button>
      </Stack>
      {setZone.error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t('common.error_generic')} {(setZone.error as Error).message}
        </Alert>
      ) : null}
    </Paper>
  );
}

/**
 * Assets tab of a place: everything that belongs here or is here now — search, state filter, zone chips (?zone=,
 * also set by a zone sign scanned with the phone camera), bulk move into a zone for people who can act here.
 */
export function PlaceAssets({ site }: { site: SiteOverview }) {
  const siteId = site.site_id;
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const board = useSiteBoard(siteId);
  const zones = useZones(siteId);
  // one fetch for the whole board, so searching and filtering reuse it
  const thumbs = useFirstPhotoUrls(
    useMemo(() => (board.data ?? []).map((a) => a.asset_id), [board.data]),
  );
  const [search, setSearch] = useState('');
  const [state, setState] = useState<AssetDisplayState | ''>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastMove, setLastMove] = useState<SetZoneResult | null>(null);
  const [printingSigns, setPrintingSigns] = useState(false);

  const active = activeZones(zones.data);
  // ?zone= (a chip, or a zone sign scanned with the phone camera): an id no longer active shows everything
  const zoneParam = params.get('zone') ?? '';
  const zoneFilter =
    zoneParam === NO_ZONE || active.some((z) => z.id === zoneParam) ? zoneParam : '';
  const counts = useMemo(() => zoneCounts(board.data ?? [], siteId), [board.data, siteId]);
  const rows = useMemo(
    () =>
      (board.data ?? []).filter(
        (a) =>
          (!state || placeState(a) === state) &&
          inZone(a, siteId, zoneFilter) &&
          (!search.trim() ||
            `${a.asset_code} ${a.description ?? ''} ${a.category_name ?? ''} ${a.zone_name ?? ''}`
              .toLowerCase()
              .includes(search.trim().toLowerCase())),
      ),
    [board.data, search, state, siteId, zoneFilter],
  );

  if (board.isLoading) return <Loading />;
  if (board.error) return <LoadError error={board.error} onRetry={() => void board.refetch()} />;
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const lang = i18n.language;
  const showZones = active.length > 0 || (board.data ?? []).some((a) => a.zone_id);
  // moving needs a zone to move into, and access to this place
  const selectable = (a: AssetOverview) =>
    a.custody_site_id === siteId && a.asset_status === 'active';
  const canMove = active.length > 0 && canActAt(me, siteId);
  const shownSelectable = rows.filter(selectable);
  const allTicked =
    shownSelectable.length > 0 && shownSelectable.every((a) => selected.has(a.asset_id));

  const detail = (a: AssetOverview) => {
    if (a.display_state === 'in_transit' && a.vehicle_ref) return a.vehicle_ref;
    if (a.custody_site_id && a.custody_site_id !== siteId)
      return t('board.custody_elsewhere', { site: a.custody_site_name });
    const [whereId, whereName] =
      a.display_state === 'untagged'
        ? [a.seen_site_id, a.seen_site_name]
        : [a.presence_site_id, a.presence_site_name];
    if (whereId && whereId !== siteId) return whereName;
    return null;
  };
  const setZoneFilter = (value: string) => {
    setSelected(new Set());
    setParams(withParam(params, 'zone', value), { replace: true });
  };
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Box data-testid="place-assets">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ mb: 1 }}>
        <TextField
          size="small"
          label={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          size="small"
          select
          label={t('board.col_state')}
          value={state}
          onChange={(e) => setState(e.target.value as AssetDisplayState | '')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {PLACE_STATES.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`state.${s}`)}
            </MenuItem>
          ))}
        </TextField>
        {/* everyone can (re)print this place's zone signs; managers add the zones */}
        {active.length ? (
          <Button
            size="small"
            startIcon={<QrCode2Icon />}
            onClick={() => setPrintingSigns(true)}
            sx={{ ml: { sm: 'auto' }, alignSelf: { xs: 'flex-start', sm: 'center' } }}
            data-testid="zone-signs"
          >
            {t('labels.zone_signs')}
          </Button>
        ) : null}
      </Stack>
      {active.length ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 1, overflowX: 'auto', pb: 0.5 }}
          data-testid="zone-chips"
        >
          {[
            { value: '', label: t('board.zone_all', { count: counts.all }) },
            ...active.map((z) => ({
              value: z.id,
              label: `${z.name} (${counts.byZone.get(z.id) ?? 0})`,
            })),
            { value: NO_ZONE, label: t('board.zone_none', { count: counts.none }) },
          ].map((c) => (
            <Chip
              key={c.value || 'all'}
              label={c.label}
              color={zoneFilter === c.value ? 'primary' : 'default'}
              variant={zoneFilter === c.value ? 'filled' : 'outlined'}
              onClick={() => setZoneFilter(c.value)}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Stack>
      ) : null}
      {canMove && selected.size > 0 ? (
        <ZoneBulkBar
          siteId={siteId}
          zones={active}
          selected={[...selected]}
          onDone={(result) => {
            setLastMove(result);
            setSelected(new Set());
          }}
        />
      ) : null}
      {lastMove ? (
        <Alert
          severity={lastMove.skipped.length ? 'warning' : 'success'}
          sx={{ mb: 1 }}
          onClose={() => setLastMove(null)}
        >
          {t('board.zone_moved', { count: lastMove.moved })}
          {lastMove.skipped.length
            ? ` · ${t('board.zone_skipped', { count: lastMove.skipped.length })}`
            : ''}
        </Alert>
      ) : null}
      {(board.data ?? []).length === 0 ? (
        <Empty>{t('board.empty')}</Empty>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {canMove ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allTicked}
                      disabled={!shownSelectable.length}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSelected((prev) => {
                          const next = new Set(prev);
                          for (const a of shownSelectable) {
                            if (on) next.add(a.asset_id);
                            else next.delete(a.asset_id);
                          }
                          return next;
                        });
                      }}
                      slotProps={{ input: { 'aria-label': t('board.select_all') } }}
                    />
                  </TableCell>
                ) : null}
                <TableCell>{t('board.col_asset')}</TableCell>
                <TableCell>{t('board.col_category')}</TableCell>
                {showZones ? <TableCell>{t('board.col_zone')}</TableCell> : null}
                <TableCell>{t('board.col_label')}</TableCell>
                <TableCell>{t('board.col_seen')}</TableCell>
                <TableCell>{t('board.col_state')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow
                  key={a.asset_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => void navigate(`/assets/${a.asset_id}`)}
                  data-testid="board-row"
                >
                  {canMove ? (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(a.asset_id)}
                        disabled={!selectable(a)}
                        onChange={(e) => toggle(a.asset_id, e.target.checked)}
                        slotProps={{
                          input: {
                            'aria-label': t('board.select_row', { asset: a.asset_code }),
                          },
                        }}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <AssetThumb url={thumbs.data?.get(a.asset_id)} />
                      <span>{a.asset_code}</span>
                    </Stack>
                  </TableCell>
                  <TableCell>{a.category_name ?? '—'}</TableCell>
                  {showZones ? (
                    <TableCell>
                      <ZoneText a={a} siteId={siteId} />
                    </TableCell>
                  ) : null}
                  <TableCell>
                    {t(`board.label_${a.label_kind ?? (a.has_electronic_tag ? 'ble' : 'qr')}`)}
                  </TableCell>
                  <TableCell title={dateTime(a.last_seen_at, lang, tz) ?? ''}>
                    {relativeTime(a.last_seen_at, lang) ?? t('assets.not_seen')}
                    {a.last_seen_at && a.last_seen_at === a.last_counted_at
                      ? ` · ${t('board.seen_counted')}`
                      : ''}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      <StateChip state={placeState(a)} detail={detail(a)} />
                      <OpenTransferChip a={a} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {printingSigns ? (
        <ZoneSignsDialog
          siteId={siteId}
          siteName={site.name}
          onClose={() => setPrintingSigns(false)}
        />
      ) : null}
    </Box>
  );
}
